import { useId, useState } from 'react';
import { friendlyErrorMessage } from '../../../shared/friendlyError';
import { HelpTooltip, WORK_LOCATION_ACKNOWLEDGEMENT_TOOLTIP } from '../../../shared/components/HelpTooltip';
import { employeeOnboardingGateway } from '../index';
import { EoReadOnly } from '../components/Fields';
import { SectionFrame } from '../components/SectionFrame';
import { formatDisplayDate } from '../format';
import type { SectionProps } from './sectionProps';

/** Figma copy, verbatim. */
export const WORK_LOCATION_ACKNOWLEDGEMENT_TEXT =
  'I acknowledge and understand that my work location may affect how my wages are taxed. If I work at a company branch or location in another state, the company may withhold applicable state and/or local taxes from my wages based on applicable payroll tax rules.';

export function WorkDetailsSection({ setup, refresh, next, back }: SectionProps) {
  const w = setup.workDetails;
  const alreadyAcknowledged = w.workLocationAcknowledgedAtUtc !== null;
  const [checked, setChecked] = useState(alreadyAcknowledged);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const checkId = useId();

  async function submit() {
    setError(null);
    if (!checked) {
      setFieldError('Please acknowledge the work location notice to continue.');
      return;
    }
    setFieldError(null);
    setBusy(true);
    try {
      // Everything else on this screen is employer-owned and read-only; the acknowledgement is the only thing the employee writes.
      if (!alreadyAcknowledged) await employeeOnboardingGateway.acknowledgeWorkLocation();
      await refresh();
      next();
    } catch (err) {
      setError(friendlyErrorMessage(err, 'We could not record your acknowledgement. Please try again.'));
    } finally {
      setBusy(false);
    }
  }

  const ssn = w.ssnLast4 ? `•••-••-${w.ssnLast4}` : null;

  return (
    <SectionFrame title="Work Details" onSubmit={submit} onBack={back} busy={busy} error={error}>
      <p className="eo-note">These details are set by your employer. If anything looks wrong, contact your administrator.</p>
      <div className="eo-grid">
        <EoReadOnly label="SSN" askKey="ssn" value={ssn} />
        <EoReadOnly label="Employment ID" askKey="employeeNumber" value={w.employeeNumber} />
        <EoReadOnly label="Role" askKey="role" value={w.role} />
        <EoReadOnly label="Job Title" askKey="jobTitle" value={w.jobTitle} />
        <EoReadOnly label="Approving Officer" askKey="approvingOfficer" value={w.approvingOfficer} />
        <EoReadOnly label="WC Class Code" askKey="wcClassCode" value={w.wcClassCode} />
        <EoReadOnly label="Hiring Date" askKey="hireDate" value={w.hireDate ? formatDisplayDate(w.hireDate) : null} />
      </div>

      <div className="eo-ack">
        <input
          id={checkId}
          type="checkbox"
          checked={checked}
          disabled={alreadyAcknowledged}
          aria-required="true"
          aria-invalid={fieldError ? true : undefined}
          onChange={(e) => {
            setChecked(e.target.checked);
            setFieldError(null);
          }}
        />
        <label htmlFor={checkId} className="eo-ack__text">
          {WORK_LOCATION_ACKNOWLEDGEMENT_TEXT}
        </label>
        <HelpTooltip label="About the work location acknowledgement" text={WORK_LOCATION_ACKNOWLEDGEMENT_TOOLTIP} />
      </div>
      {alreadyAcknowledged && w.workLocationAcknowledgedAtUtc && (
        <p className="eo-field__hint" data-testid="acknowledged-on">
          Acknowledged on {formatDisplayDate(w.workLocationAcknowledgedAtUtc)}.
        </p>
      )}
      {fieldError && (
        <p className="eo-field__error" role="alert">
          {fieldError}
        </p>
      )}
    </SectionFrame>
  );
}
